import React, { useState, useEffect } from 'react';
import { useTutorial } from '../contexts/TutorialContext';
import { getAchievements, getPlayerStats, Achievement, PlayerStats } from '../utils/achievements';
import './Achievements.css';

const Achievements: React.FC = () => {
  const { checkAndStartTutorial } = useTutorial();
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [playerStats, setPlayerStats] = useState<PlayerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  useEffect(() => {
    loadAchievements();
  }, []);

  // Auto-start achievements tutorial when first viewing achievements page
  useEffect(() => {
    if (achievements.length > 0 && !loading) {
      checkAndStartTutorial('achievements-basics');
    }
  }, [achievements, loading, checkAndStartTutorial]);

  const loadAchievements = async () => {
    setLoading(true);
    try {
      const [achievementsData, statsData] = await Promise.all([
        getAchievements(),
        getPlayerStats()
      ]);
      console.log('Achievements data received:', achievementsData);
      console.log('Stats data received:', statsData);
      console.log('Achievements count:', achievementsData?.length);
      setAchievements(achievementsData);
      setPlayerStats(statsData);
    } catch (error) {
      console.error('Error loading achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatValue = (statKey: string): number => {
    if (!playerStats) return 0;
    switch (statKey) {
      case 'BattlesWon': return playerStats.battlesWon;
      case 'BattlesLost': return playerStats.battlesLost;
      case 'MonstersDefeated': return playerStats.monstersDefeated;
      case 'TotalDamageDealt': return playerStats.totalDamageDealt;
      case 'ItemsCrafted': return playerStats.itemsCrafted;
      case 'RecipesDiscovered': return playerStats.recipesDiscovered;
      case 'TilesExplored': return playerStats.tilesExplored;
      case 'MaterialsGathered': return playerStats.materialsGathered;
      default: return 0;
    }
  };

  const categories = ['All', ...Array.from(new Set(achievements.map(a => a.category)))];

  const filteredAchievements = selectedCategory === 'All'
    ? achievements
    : achievements.filter(a => a.category === selectedCategory);

  const unlockedCount = achievements.filter(a => a.unlocked).length;
  const totalCount = achievements.length;
  const completionPercentage = totalCount > 0 ? Math.round((unlockedCount / totalCount) * 100) : 0;

  if (loading) {
    return (
      <div className="achievements-container">
        <div className="loading">Loading achievements...</div>
      </div>
    );
  }

  return (
    <div className="achievements-container">
      <div className="achievements-header">
        <h2>🏆 Achievements</h2>
        <div className="achievements-stats">
          <div className="stat-card">
            <div className="stat-value">{unlockedCount}/{totalCount}</div>
            <div className="stat-label">Unlocked</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{completionPercentage}%</div>
            <div className="stat-label">Complete</div>
          </div>
        </div>
      </div>

      <div className="achievements-progress-bar">
        <div
          className="achievements-progress-fill"
          style={{ width: `${completionPercentage}%` }}
        />
      </div>

      <div className="achievements-categories">
        {categories.map(category => (
          <button
            key={category}
            className={`category-button ${selectedCategory === category ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>

      <div className="achievements-grid">
        {filteredAchievements.map(achievement => {
          const currentProgress = getStatValue(achievement.statKey);
          const progressPercent = Math.min(
            Math.round((currentProgress / achievement.requiredValue) * 100),
            100
          );

          return (
            <div
              key={achievement.id}
              className={`achievement-card ${achievement.unlocked ? 'unlocked' : 'locked'}`}
            >
              <div className="achievement-icon">
                {achievement.icon}
              </div>
              <div className="achievement-content">
                <h3 className="achievement-name">{achievement.name}</h3>
                <p className="achievement-description">{achievement.description}</p>
                <div className="achievement-category-badge">{achievement.category}</div>

                {achievement.unlocked ? (
                  <div className="achievement-unlocked-info">
                    <span className="unlocked-badge">✓ Unlocked</span>
                    {achievement.unlockedAt && (
                      <span className="unlocked-date">
                        {new Date(achievement.unlockedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="achievement-progress">
                    <div className="progress-text">
                      {currentProgress} / {achievement.requiredValue}
                    </div>
                    <div className="progress-bar-container">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                    <div className="progress-percentage">{progressPercent}%</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredAchievements.length === 0 && (
        <div className="no-achievements">
          <p>No achievements found in this category.</p>
        </div>
      )}

      {playerStats && (
        <div className="player-stats-summary">
          <h3>Your Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-icon">⚔️</span>
              <span className="stat-name">Battles Won</span>
              <span className="stat-number">{playerStats.battlesWon}</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">💀</span>
              <span className="stat-name">Monsters Defeated</span>
              <span className="stat-number">{playerStats.monstersDefeated}</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">🔨</span>
              <span className="stat-name">Items Crafted</span>
              <span className="stat-number">{playerStats.itemsCrafted}</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">📜</span>
              <span className="stat-name">Recipes Discovered</span>
              <span className="stat-number">{playerStats.recipesDiscovered}</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">🗺️</span>
              <span className="stat-name">Tiles Explored</span>
              <span className="stat-number">{playerStats.tilesExplored}</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">⛏️</span>
              <span className="stat-name">Materials Gathered</span>
              <span className="stat-number">{playerStats.materialsGathered}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Achievements;
